import { useCallback } from "react";
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { useEnabledModules } from "@/hooks/use-enabled-modules";
import {
  VISHING_MODULE_KEY,
  moduleIsOn,
  offeredRuleFields,
} from "@/lib/recipients/group-rule-fields";
import { fetchApi, buildQueryString } from "@/lib/api/client";
import { recipientsFetch } from '@/lib/recipients/api';
import { toast } from "sonner";
import { groupKeys, recipientKeys } from "@/hooks/api/query-keys";
import type { RecipientList, Recipient } from "@/lib/types/recipients";
import { normalizeRecipient } from "@/lib/utils/normalize-recipient";
import type { PaginatedResult } from "@/hooks/api/pagination";
import { lmsLearnerKeys } from "@/components/lms/hooks/lms-learner-keys";

/**
 * PROD-1606 — the course Enrol dialog reads group memberships off the LMS
 * learner roster, which the course page holds for 5 minutes without a
 * refetch on focus. Every group change marks it stale so a new group, or a
 * member added to one, reaches the dialog without a reload.
 */
function invalidateLmsLearners(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: lmsLearnerKeys.all() });
}

// ============ Shared Types ============

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface GroupMembersFilter {
  page?: number;
  limit?: number;
  search?: string;
}

// ============ Query Hooks ============

/**
 * Returns ALL groups as a flat array (genuinely unpaginated). Used in filter
 * dropdowns and pickers that need the complete set — campaign-wizard
 * GroupsPanel / SmartGroupsPanel, recipient filter dropdowns, etc.
 *
 * `GET /v1/recipients/groups` is server-paginated (default limit=25). This
 * hook USED to fetch a single page and return only `.data` — so its JSDoc
 * promise of "all groups" was a lie: workspaces with >25 groups silently
 * hid the rest, and a newly-created group could be invisible in every group
 * picker (same bug class as ticket #1606's learner truncation). We now page
 * through all results at the endpoint's max page size and concatenate.
 */
export function useGroups(filter?: Record<string, unknown>) {
  return useQuery({
    queryKey: groupKeys.list(filter),
    queryFn: async () => {
      const LIMIT = 100; // /recipients/groups max page size
      const all: RecipientList[] = [];
      let page = 1;
      for (let i = 0; i < 100; i++) {
        const qs = buildQueryString({ ...(filter ?? {}), page, limit: LIMIT });
        const result = await recipientsFetch<
          { data: RecipientList[]; total?: number } | RecipientList[]
        >(`groups${qs}`);
        const batch = Array.isArray(result) ? result : (result?.data ?? []);
        all.push(...batch);
        const total = Array.isArray(result) ? undefined : result?.total;
        // Stop on the last (partial) page, or once we've collected `total`.
        if (batch.length < LIMIT) break;
        if (typeof total === "number" && all.length >= total) break;
        page += 1;
      }
      return all;
    },
  });
}

/**
 * Segmentation rule-field catalog (behavioural: phishing / history / training /
 * risk) for the dynamic-group rule builder. Served by the ALWAYS-ON recipients
 * module (`GET /recipients/groups/rule-fields`) so these fields are available
 * for grouping even when the separately-entitled workflows module is OFF — the
 * builder used to source them from GET /workflows/registry, which ModuleAccessGuard
 * 403s when workflows is disabled. Shape mirrors the workflows registry's
 * `{ ruleFields, ruleFieldCategories }`. Cached 5 min.
 *
 * The catalog is per TENANT: the API adds the Vishing-results fields only for
 * a tenant that has the Vishing module. They are also dropped here unless the
 * tenant's module set is known to have it, so a catalog cached before the
 * module was switched off does not keep offering them.
 */
export function useGroupRuleFields() {
  const vishingOn = moduleIsOn(useEnabledModules(), VISHING_MODULE_KEY);
  const select = useCallback(
    (catalog: {
      ruleFields: Record<string, unknown>[];
      ruleFieldCategories: unknown;
    }) => ({
      ...catalog,
      ruleFields: offeredRuleFields(catalog.ruleFields ?? [], vishingOn),
    }),
    [vishingOn],
  );
  return useQuery({
    queryKey: [...groupKeys.all(), "rule-fields"] as const,
    queryFn: () =>
      fetchApi<{
        ruleFields: Record<string, unknown>[];
        ruleFieldCategories: unknown;
      }>("/recipients/groups/rule-fields"),
    select,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

/** Paginated — returns { data, total, page, limit, totalPages }. Used by the groups table. */
export function useGroupsPaginated(
  filter?: Record<string, unknown> & {
    page?: number;
    limit?: number;
    search?: string;
    type?: string[];
    sourceType?: string[];
    from?: string;
    to?: string;
  },
) {
  const defaultLimit = filter?.limit ?? 25;
  return useQuery({
    queryKey: [...groupKeys.lists(), "paginated", filter],
    queryFn: async () => {
      const qs = filter ? buildQueryString(filter) : "";
      return recipientsFetch<PaginatedResponse<RecipientList> | RecipientList[]>(
        `groups${qs}`,
      );
    },
    select: (raw): PaginatedResult<RecipientList> => {
      if (Array.isArray(raw)) {
        return {
          data: raw,
          total: raw.length,
          page: 1,
          limit: raw.length || defaultLimit,
          totalPages: 1,
        };
      }
      const r = raw as PaginatedResponse<RecipientList>;
      const limit = r.limit ?? defaultLimit;
      const total = r.total ?? 0;
      return {
        data: r.data ?? [],
        total,
        page: r.page ?? 1,
        limit,
        totalPages: r.totalPages ?? Math.max(1, Math.ceil(total / limit)),
      };
    },
  });
}

export function useGroup(id: string | undefined) {
  return useQuery({
    queryKey: groupKeys.detail(id!),
    queryFn: () => recipientsFetch<RecipientList>(`groups/${id}`),
    enabled: !!id,
  });
}

/** Response of `GET /v1/recipients/groups/:id/member-ids`. */
export interface GroupMemberIds {
  /** recipient.id for every member — capped server-side at MEMBER_IDS_MAX. */
  recipientIds: string[];
  /** The TRUE membership size, even when the id list was capped. */
  total: number;
  /** `true` ⇒ `recipientIds` is a PREFIX, never diff a save against it. */
  truncated: boolean;
}

/**
 * The FULL member recipientId set for a group — the honest baseline for an
 * add/remove diff.
 *
 * #238: this used to hit `GET /recipients/groups/:id/members`, whose response
 * is PAGINATED (default 10, clamped at 100). It mapped `raw.data` to ids and
 * returned them as if they were the whole membership, so any group larger than
 * one page produced a silently short baseline. The dedicated `:id/member-ids`
 * endpoint (phase 1) returns the whole set plus an explicit `truncated` flag,
 * so a caller can refuse to diff instead of computing a wrong `removed` list.
 *
 * The query key stays `groupKeys.members(id)` — the add/remove mutations below
 * already invalidate exactly that, and this must refetch when they land.
 */
export function useGroupMemberIds(id: string | undefined) {
  return useQuery({
    queryKey: groupKeys.members(id!),
    queryFn: () =>
      fetchApi<GroupMemberIds>(`/recipients/groups/${id}/member-ids`),
    enabled: !!id,
  });
}

/** Returns paginated Recipient objects for group members. */
export function useGroupMembersList(
  id: string | undefined,
  filter?: GroupMembersFilter,
) {
  const defaultLimit = filter?.limit ?? 10;
  return useQuery({
    queryKey: [...groupKeys.members(id!), "full", filter],
    queryFn: async () => {
      const qs = filter
        ? buildQueryString(filter as Record<string, unknown>)
        : "";
      return fetchApi<
        | Array<Record<string, unknown>>
        | PaginatedResponse<Record<string, unknown>>
      >(`/recipients/groups/${id}/members${qs}`);
    },
    enabled: !!id,
    // Bug #127 — keep the previous page mounted while the next search fires
    // so the search Input doesn't unmount between keystrokes when a query
    // temporarily returns zero results.
    placeholderData: keepPreviousData,
    select: (raw): PaginatedResult<Recipient> => {
      if (Array.isArray(raw)) {
        return {
          data: raw.map((item) =>
            normalizeRecipient(item as Record<string, unknown>),
          ),
          total: raw.length,
          page: 1,
          limit: raw.length || defaultLimit,
          totalPages: 1,
        };
      }

      const limit = raw.limit ?? defaultLimit;
      const total = raw.total ?? 0;
      return {
        data: (raw.data ?? []).map((item) =>
          normalizeRecipient(item as Record<string, unknown>),
        ),
        total,
        page: raw.page ?? 1,
        limit,
        totalPages: raw.totalPages ?? Math.max(1, Math.ceil(total / limit)),
      };
    },
  });
}

/**
 * @deprecated Use useGroupMemberIds (returns `{ recipientIds, total,
 * truncated }`, NOT a bare string[]) or useGroupMembersList.
 */
export function useGroupMembers(id: string | undefined) {
  return useGroupMemberIds(id);
}

/** The five KPI figures, in one scope. */
export interface GroupMetricsFigures {
  totalGroups: number;
  staticGroups: number;
  dynamicGroups: number;
  totalMemberships: number;
  uniqueMembers: number;
}

/**
 * What `GET /recipients/groups/metrics` returns (#237).
 *
 * The top-level block is WORKSPACE-WIDE — it ignores search/sourceType/date.
 * It used to be the filtered block, which is why all four cards rewrote
 * themselves under a search while their subtitles still said "Showing all
 * groups" / "total across all groups". `filtered` is null when no narrowing
 * filter was sent (filtered === workspace-wide), so the card renders its
 * static descriptor rather than a disclosure with nothing to disclose.
 */
export interface GroupMetricsPayload extends GroupMetricsFigures {
  averageMemberCount: number;
  filtered: GroupMetricsFigures | null;
}

/**
 * KPI cards for the groups page. Accepts the SAME table filters
 * (search/sourceType/dateRange) so the summary matches the filtered list
 * (email-logs #1762 pattern). The BE deliberately ignores the `type` filter
 * for these cards — the Total/Static/Dynamic cards double as the type-facet
 * selector, so the type breakdown stays constant — but we still forward the
 * full filter object; the BE drops `type` server-side.
 */
export function useGroupMetrics(
  filter?: Record<string, unknown> & {
    search?: string;
    sourceType?: string[];
    from?: string;
    to?: string;
  },
) {
  return useQuery({
    queryKey: groupKeys.metrics(filter),
    queryFn: () => {
      const qs = filter ? buildQueryString(filter) : "";
      return fetchApi<GroupMetricsPayload>(`/recipients/groups/metrics${qs}`);
    },
  });
}

// ============ Mutation Hooks ============

export function useCreateGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<RecipientList>) =>
      recipientsFetch<RecipientList>("groups", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      invalidateLmsLearners(queryClient);
      queryClient.invalidateQueries({ queryKey: groupKeys.all() });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

export function useUpdateGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<RecipientList> }) =>
      recipientsFetch<RecipientList>(`groups/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, { id }) => {
      invalidateLmsLearners(queryClient);
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
      queryClient.invalidateQueries({ queryKey: groupKeys.metricsAll() });
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

export function useDeleteGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      recipientsFetch<void>(`groups/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      invalidateLmsLearners(queryClient);
      queryClient.invalidateQueries({ queryKey: groupKeys.all() });
      queryClient.invalidateQueries({ queryKey: groupKeys.metricsAll() });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

/**
 * A group POST /recipients/groups/bulk-delete kept, and why — mirrors the API's
 * BulkDeleteSkippedGroup (groups.service.ts): a default group is never deleted,
 * nor is one anything still reads (GR15). `reason` is "workflows" when only
 * workflows read it, "references" when anything else does.
 */
export interface BulkDeleteSkippedGroup {
  id: string;
  name: string;
  reason: "default" | "workflows" | "references";
  /** The workflows using it — empty when none do. */
  workflows: string[];
  /** Everything that still reads it: `scope` is the kind ("dynamic group"), `message` its name. */
  references: { scope: string; message: string }[];
}

/**
 * FE17 (audit 2026-09-25) — one request for a bulk group delete, answering how
 * many were deleted and which were kept. The lists are re-read however it
 * ends, so a kept or failed group is never left looking deleted.
 */
export function useBulkDeleteGroups() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      recipientsFetch<{ deleted: number; skipped: BulkDeleteSkippedGroup[] }>(
        "groups/bulk-delete",
        { method: "POST", body: JSON.stringify({ ids }) },
      ),
    onSettled: () => {
      invalidateLmsLearners(queryClient);
      queryClient.invalidateQueries({ queryKey: groupKeys.all() });
      queryClient.invalidateQueries({ queryKey: groupKeys.metricsAll() });
      queryClient.invalidateQueries({ queryKey: recipientKeys.lists() });
    },
  });
}

export function useAddGroupMembers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      recipientIds,
    }: {
      id: string;
      recipientIds: string[];
    }) =>
      recipientsFetch<{ added: number }>(`groups/${id}/members`, {
        method: "POST",
        body: JSON.stringify({ recipientIds }),
      }),
    onSuccess: (_data, { id }) => {
      invalidateLmsLearners(queryClient);
      queryClient.invalidateQueries({ queryKey: groupKeys.members(id) });
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

export function useRemoveGroupMembers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      recipientIds,
    }: {
      id: string;
      recipientIds: string[];
    }) =>
      recipientsFetch<{ removed: number }>(
        `groups/${id}/members/remove`,
        {
          method: "POST",
          body: JSON.stringify({ recipientIds }),
        },
      ),
    onSuccess: (_data, { id }) => {
      invalidateLmsLearners(queryClient);
      queryClient.invalidateQueries({ queryKey: groupKeys.members(id) });
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(id) });
      // Invalidate all group list queries (including paginated) so member counts refresh
      queryClient.invalidateQueries({ queryKey: groupKeys.lists() });
      queryClient.invalidateQueries({ queryKey: groupKeys.metricsAll() });
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

export function useDuplicateGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name?: string }) =>
      recipientsFetch<RecipientList>(`groups/${id}/duplicate`, {
        method: "POST",
        body: JSON.stringify({ name }),
      }),
    onSuccess: () => {
      invalidateLmsLearners(queryClient);
      queryClient.invalidateQueries({ queryKey: groupKeys.all() });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

export function useMergeGroups() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      primaryId: string;
      secondaryIds: string[];
      name?: string;
    }) =>
      recipientsFetch<RecipientList>("groups/merge", {
        method: "POST",
        body: JSON.stringify({
          sourceIds: data.secondaryIds,
          targetName: data.name ?? data.primaryId,
        }),
      }),
    onSuccess: () => {
      invalidateLmsLearners(queryClient);
      queryClient.invalidateQueries({ queryKey: groupKeys.all() });
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
    },
    // A refused merge (an exclusive category, 2026-10-02) wrote nothing, but
    // the groups page has already drawn it: refetch so the screen shows the
    // groups as they are. The caller says why, in the reader's language
    // (groupMergeErrorMessage) — never the API's English message.
    onError: () => {
      queryClient.invalidateQueries({ queryKey: groupKeys.all() });
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
    },
  });
}

export function useRefreshGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      recipientsFetch<{ recipientCount: number }>(`groups/${id}/refresh`, {
        method: "POST",
      }),
    onSuccess: (_data, id) => {
      invalidateLmsLearners(queryClient);
      queryClient.invalidateQueries({ queryKey: groupKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: groupKeys.members(id) });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}
